import { createContext } from 'react';

import type { IStep } from '@chainlit/react-client';

/**
 * Which status line is the thread's latest, and the question it stands for.
 *
 * Worked out once per message list by the container, and kept to two strings
 * so the context value changes only when a status line comes or goes: every
 * message reads it, and a value that changed on every streamed token would
 * re-render the whole thread per token.
 */
export interface StatusInfo {
  latestStatusId?: string;
  question?: string;
  /** The thread these messages belong to, when it is not the chat's own
   *  (the read-only view shown while a thread is being reopened). */
  threadId?: string;
  /** No composer to send "Ask again" to. */
  readOnly?: boolean;
}

export const StatusContext = createContext<StatusInfo>({});

const isStatus = (step: IStep) => {
  const phase = (step.metadata as Record<string, unknown> | undefined)
    ?.signals_status;
  return typeof phase === 'string' && !!phase;
};

export function findLatestStatus(messages: IStep[]): StatusInfo {
  let latestStatusId: string | undefined;
  let question: string | undefined;
  let lastQuestion: string | undefined;
  const walk = (steps: IStep[]) => {
    for (const step of steps) {
      if (step.type === 'user_message' && step.output?.trim()) {
        lastQuestion = step.output.trim();
      } else if (step.type.includes('message') && isStatus(step)) {
        latestStatusId = step.id;
        question = lastQuestion;
      }
      if (step.steps?.length) walk(step.steps);
    }
  };
  walk(messages);
  return { latestStatusId, question };
}
